<template>
  <div class="orcamento-itens">
    <div class="orcamento-itens__header">
      <div class="orcamento-itens__title-wrap">
        <div class="orcamento-itens__title">
          Itens do Orçamento
          <span class="orcamento-itens__filial ml-2"> {{ filial }}</span>
        </div>
        <div class="orcamento-itens__subtitle">
          {{ items.length }} produto(s)
        </div>
      </div>
      <div class="orcamento-itens__header-actions">
        <b-button
          v-if="exibirAcoes"
          v-on:click="$emit('add-products')"
          size="sm"
          class="orcamento-itens__add-btn"
          variant="primary"
          :disabled="!modoEdicao"
        >
          <i class="fa fa-plus"></i> Adicionar Produtos
        </b-button>
      </div>
    </div>

    <div class="orcamento-itens__body">
      <span v-if="loading" class="orcamento-itens__loading">
        <svg width="40" height="40" viewBox="0 0 50 50">
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="currentColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-dasharray="31.4 31.4"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
        Carregando itens...
      </span>

      <b-table
        v-show="!loading"
        class="w-100 orcamento-itens__table mb-0"
        :items="sortedItems"
        :fields="tableFields"
        primary-key="id"
        v-on:sort-changed="onTableSortChanged"
        small
        hover
        :responsive="true"
        :sort-by="localSortBy"
        :sort-desc="localSortDesc"
        :no-local-sorting="true"
        :foot-clone="true"
        :no-footer-sorting="true"
        :no-sort-reset="true"
        stacked="sm"
        ref="cartTable"
      >
        <template #thead-top="">
          <b-tr>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('produto_codigo')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('produto_codigo')"
              @keydown.enter.prevent="toggleSort('produto_codigo')"
              @keydown.space.prevent="toggleSort('produto_codigo')"
            >
              Produto
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('quantidade')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('quantidade')"
              @keydown.enter.prevent="toggleSort('quantidade')"
              @keydown.space.prevent="toggleSort('quantidade')"
            >
              Qtde
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--custo" colspan="2">
              Custo
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--tributos" colspan="3">
              Tributos
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--margem" colspan="3">
              Margem
            </b-th>
            <b-th class="orcamento-itens__group orcamento-itens__group--premio" colspan="2">
              Total Prêmio
            </b-th>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--unitario orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('valor_unit_total')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('valor_unit_total')"
              @keydown.enter.prevent="toggleSort('valor_unit_total')"
              @keydown.space.prevent="toggleSort('valor_unit_total')"
            >
              Valor c/ Margem
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--venda orcamento-itens__group--solo orcamento-itens__group--sortable"
              :class="sortHeaderClass('valor_total')"
              role="columnheader"
              tabindex="0"
              @click="toggleSort('valor_total')"
              @keydown.enter.prevent="toggleSort('valor_total')"
              @keydown.space.prevent="toggleSort('valor_total')"
            >
              Valor Venda
              <span class="orcamento-itens__sort-icon" aria-hidden="true"></span>
            </b-th>
            <b-th
              v-if="exibirAcoes"
              rowspan="2"
              class="orcamento-itens__group orcamento-itens__group--solo orcamento-itens__group--acoes"
            >
              Ações
            </b-th>
          </b-tr>
        </template>

        <template v-slot:cell(produto_codigo)="data">
          <div class="orcamento-itens__produto">
            <div class="orcamento-itens__produto-linha">
              <span class="orcamento-itens__codigo">{{ data.item.produto_codigo }}</span>
              <span class="orcamento-itens__nome">{{ data.item.titulo }}</span>
              <slot name="alerta" :item="data.item"></slot>
            </div>
            <span
              v-if="data.item.qt_estoque_disponivel != null && data.item.qt_estoque_disponivel !== ''"
              class="orcamento-itens__estoque"
            >
              Estoque: {{ data.item.qt_estoque_disponivel }}
            </span>
          </div>
        </template>

        <template v-slot:cell(quantidade)="data">
          <b-form-input
            v-if="modoEdicao"
            type="number"
            :min="data.item.multiplo || 1"
            :step="data.item.multiplo || 1"
            :value="quantidadeExibida(data.item)"
            :state="quantidadeState(data.item)"
            class="orcamento-itens__qtde"
            @input="onQuantidadeInput(data.item, $event)"
            @blur="onQuantidadeBlur(data.item)"
          />
          <span v-else class="orcamento-itens__num">{{ data.item.quantidade }}</span>
        </template>

        <template v-slot:cell(valor_unitario)="data">
          <span class="orcamento-itens__money">{{ data.item.valor_unitario | money }}</span>
        </template>

        <template v-slot:cell(total_custo)="data">
          <span class="orcamento-itens__money orcamento-itens__money--custo-total ">{{ totalCusto(data.item) | money }}</span>
        </template>

        <template v-slot:cell(valor_ipi)="data">
          <b-spinner v-if="recalculandoMargemItem(data.item)" small variant="secondary"></b-spinner>
          <span v-else class="orcamento-itens__money">{{ ipiUnit(data.item) | money }}</span>
        </template>

        <template v-slot:cell(valor_st)="data">
          <b-spinner v-if="recalculandoMargemItem(data.item)" small variant="secondary"></b-spinner>
          <span v-else class="orcamento-itens__money">{{ stUnit(data.item) | money }}</span>
        </template>

        <template v-slot:cell(total_trib)="data">
          <b-spinner v-if="recalculandoMargemItem(data.item)" small variant="secondary"></b-spinner>
          <span v-else class="orcamento-itens__money orcamento-itens__money--trib">
            {{ totalTrib(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(margem_valor)="data">
          <b-form-input
            v-if="modoEdicao"
            type="number"
            min="0"
            step="0.01"
            :value="margemValorExibida(data.item)"
            :state="margemValorState(data.item)"
            class="orcamento-itens__margem"
            @input="onMargemValorInput(data.item, $event)"
            @blur="onMargemValorBlur(data.item)"
          />
          <span v-else class="orcamento-itens__money">{{ data.item.margem_valor | money }}</span>
        </template>

        <template v-slot:cell(margem_percentual)="data">
          <div v-if="modoEdicao" class="orcamento-itens__percent">
            <b-form-input
              type="text"
              inputmode="decimal"
              :value="margemPercentualExibida(data.item)"
              :state="margemPercentualState(data.item)"
              class="orcamento-itens__margem orcamento-itens__margem--percent"
              @input="onMargemPercentualInput(data.item, $event)"
              @blur="onMargemPercentualBlur(data.item)"
            />
            <span class="orcamento-itens__percent-sign" aria-hidden="true">%</span>
          </div>
          <span v-else class="orcamento-itens__num">{{ formatPercent(data.item.margem_percentual) }}</span>
        </template>

        <template v-slot:cell(margem_total)="data">
          <span class="orcamento-itens__money orcamento-itens__money--margem">
            {{ margemTotal(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(premio_unit)="data">
          <span class="orcamento-itens__money">{{ premioUnit(data.item) | money }}</span>
        </template>

        <template v-slot:cell(valor_comissao)="data">
          <span class="orcamento-itens__money orcamento-itens__money--premio">
            {{ premioTotal(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(valor_unit_total)="data">
          <span class="orcamento-itens__money orcamento-itens__money--venda">
            {{ valorVendaUnitario(data.item) | money }}
          </span>
        </template>

        <template v-slot:cell(valor_total)="data">
          <span class="orcamento-itens__money orcamento-itens__money--venda">
            {{ data.item.valor_total | money }}
          </span>
        </template>

        <template v-slot:cell(id)="data">
          <div class="orcamento-itens__acoes">
            <b-button
              class="orcamento-itens__acao orcamento-itens__acao--delete"
              size="sm"
              :disabled="!modoEdicao"
              v-on:click="$emit('delete', data.item)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
        </template>

        <template v-slot:foot()>
          <span></span>
        </template>
        <template v-slot:foot(produto_codigo)>
          <span class="orcamento-itens__totais-label">Resumo dos Totais</span>
        </template>
        <template v-slot:foot(quantidade)>
          <span class="orcamento-itens__num">{{ qtdeTotal }}</span>
        </template>
        <template v-slot:foot(total_custo)>
          <span class="orcamento-itens__money orcamento-itens__money--custo-total" v-if="!itensAlterados">
            {{ custoTotalSum | money }}
          </span>
          <span v-else class="orcamento-itens__spinner">
            <svg width="18" height="18" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        </template>
        <template v-slot:foot(valor_ipi)>
          <span></span>
        </template>
        <template v-slot:foot(valor_st)>
          <span></span>
        </template>
        <template v-slot:foot(total_trib)>
          <span class="orcamento-itens__money orcamento-itens__money--trib" v-if="!itensAlterados">
            {{ tribTotalSum | money }}
          </span>
        </template>
        <template v-slot:foot(margem_total)>
          <span class="orcamento-itens__money orcamento-itens__money--margem" v-if="!itensAlterados">
            {{ margemTotalSum | money }}
          </span>
        </template>
        <template v-slot:foot(valor_comissao)>
          <span class="orcamento-itens__money orcamento-itens__money--premio" v-if="!itensAlterados">
            {{ comissaoTotalSum | money }}
          </span>
          <span v-else class="orcamento-itens__spinner">
            <svg width="18" height="18" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        </template>
        <template v-slot:foot(valor_unit_total)>
          <span class="orcamento-itens__money orcamento-itens__money--unitario">
            {{ valorUnitTotalSum | money }}
          </span>
        </template>
        <template v-slot:foot(valor_total)>
          <span class="orcamento-itens__money orcamento-itens__money--venda" v-if="!itensAlterados">
            {{ valorTotalSum | money }}
          </span>
          <span v-else class="orcamento-itens__spinner">
            <svg width="18" height="18" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                stroke-linecap="round"
                stroke-dasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </span>
        </template>
      </b-table>
    </div>

    <div class="orcamento-itens__footer">
      <div class="orcamento-itens__note">
        * ICMS ST/DIFAL, quando aplicável, será de responsabilidade do destinatário.
      </div>
      <div v-if="modoEdicao && temAlteracoesPendentes" class="orcamento-itens__save-wrap">
        <span class="orcamento-itens__pendente">
          <span class="orcamento-itens__pendente-dot" aria-hidden="true"></span>
          Alterações não salvas (salve ou desfaça)
        </span>
        <small v-if="temMultiploInvalido" class="orcamento-itens__qtde-erro">
          Há algum produto com quantidade não adequada ao seu múltiplo.
        </small>
        <small v-if="temMargemForaLimite" class="orcamento-itens__qtde-erro">
          Há margem fora do limite permitido.
        </small>
        <b-button
          size="sm"
          class="orcamento-itens__cancel-btn"
          :disabled="itensAlterados"
          @click="confirmarCancelarAlteracoes"
        >
          Desfazer Alterações
        </b-button>
        <b-button
          size="sm"
          class="orcamento-itens__save-btn"
          :disabled="!podeSalvarItens || itensAlterados"
          @click="salvarQuantidades"
        >
          <i class="fa fa-save"></i> Salvar Alterações
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import Produto from "@/services/produto";

export default {
  name: "OrcamentoItensTabela",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    filial: {
      type: String,
      default: "",
    },
    modoEdicao: {
      type: Boolean,
      default: false,
    },
    exibirAcoes: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    itensAlterados: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: "produto_codigo",
    },
    sortDirection: {
      type: String,
      default: "asc",
    },
    getComissaoFn: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      localSortBy: this.sortBy,
      localSortDesc: this.sortDirection === "desc",
      quantidadesBase: {},
      quantidadesDigitadas: {},
      margensValorDigitadas: {},
      margensPercentualDigitadas: {},
      recalculoMargemToken: {},
      recalculandoMargemIds: {},
      recalculoMargemTimers: {},
    };
  },
  beforeDestroy() {
    this.limparEstadoRecalculoMargem();
  },
  watch: {
    items: {
      immediate: true,
      handler(items, oldItems) {
        const lista = items || [];
        const substituida = !oldItems || items !== oldItems;
        if (substituida) {
          this.limparEstadoRecalculoMargem();
          const bases = {};
          lista.forEach((item) => {
            this.normalizarMargemValor(item);
            this.garantirPercentual(item);
            bases[item.id] = this.criarBaseQuantidade(item);
          });
          this.quantidadesBase = bases;
          this.quantidadesDigitadas = {};
          this.margensValorDigitadas = {};
          this.margensPercentualDigitadas = {};
          return;
        }
        lista.forEach((item) => {
          if (!this.quantidadesBase[item.id]) {
            this.normalizarMargemValor(item);
            this.garantirPercentual(item);
            this.$set(this.quantidadesBase, item.id, this.criarBaseQuantidade(item));
          }
        });
      },
    },
    temAlteracoesPendentes(pendente) {
      this.$emit("alteracoes-pendentes", pendente);
    },
    sortBy(value) {
      if (value !== this.localSortBy) {
        this.localSortBy = value;
      }
    },
    sortDirection(value) {
      const desc = value === "desc";
      if (desc !== this.localSortDesc) {
        this.localSortDesc = desc;
      }
    },
  },
  computed: {
    sortedItems() {
      const key = this.localSortBy;
      if (!key) {
        return this.items;
      }

      const items = this.items.slice();
      const desc = this.localSortDesc;

      items.sort((a, b) => {
        const result = this.compareSortValues(this.valorOrdenacao(a, key), this.valorOrdenacao(b, key));
        return desc ? -result : result;
      });

      return items;
    },
    tableFields() {
      const fields = [
        {
          key: "produto_codigo",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--spanned",
          tdClass: "orcamento-itens__td orcamento-itens__td--produto",
        },
        {
          key: "quantidade",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-center",
        },
        {
          key: "valor_unitario",
          label: "Vl. Unit. S/ IPI",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--custo text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "total_custo",
          label: "Total Custo",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--custo text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_ipi",
          label: "IPI Unit.",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--tributos text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_st",
          label: "ST Unit.*",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--tributos text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "total_trib",
          label: "Total Trib.",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--tributos text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "margem_valor",
          label: "Margem Unit.",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--margem text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "margem_percentual",
          label: "% Unit.",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--margem text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "margem_total",
          label: "Margem Total",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--margem text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "premio_unit",
          label: "Prêmio Unit.",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--premio text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_comissao",
          label: "Total Prêmio",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--premio text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_unit_total",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--unitario orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-right",
        },
        {
          key: "valor_total",
          label: "",
          sortable: true,
          thClass: "orcamento-itens__th orcamento-itens__th--venda orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-right",
        },
      ];

      if (this.exibirAcoes) {
        fields.push({
          key: "id",
          label: "",
          sortable: false,
          thClass: "orcamento-itens__th orcamento-itens__th--spanned text-center",
          tdClass: "orcamento-itens__td text-center",
        });
      }

      return fields;
    },
    qtdeTotal() {
      return this.items.reduce((total, item) => total + item.quantidade * 1, 0);
    },
    custoTotalSum() {
      return this.items.reduce((total, item) => total + this.totalCusto(item), 0);
    },
    tribTotalSum() {
      return this.items.reduce((total, item) => total + this.totalTrib(item), 0);
    },
    margemTotalSum() {
      return this.items.reduce((total, item) => total + this.margemTotal(item), 0);
    },
    comissaoTotalSum() {
      return this.items.reduce((total, item) => total + this.premioTotal(item), 0);
    },
    valorUnitTotalSum() {
      return this.items.reduce((total, item) => total + this.valorVendaUnitario(item), 0);
    },
    valorTotalSum() {
      return this.items.reduce((total, item) => total + item.valor_total * 1, 0);
    },
    temAlteracaoQuantidade() {
      return this.items.some((item) => {
        const base = this.quantidadesBase[item.id];
        return base && item.quantidade * 1 !== base.quantidade * 1;
      });
    },
    temQuantidadeInvalida() {
      return this.items.some((item) => this.quantidadeBloqueiaSalvar(item));
    },
    temMultiploInvalido() {
      return this.items.some((item) => this.quantidadeMultiploInvalido(item));
    },
    temAlteracaoMargem() {
      return this.items.some((item) => this.margemAlterada(item));
    },
    temMargemInvalida() {
      return this.items.some((item) => this.margemBloqueiaSalvar(item));
    },
    temMargemForaLimite() {
      return this.items.some((item) => this.margemAlterada(item) && this.margemForaLimite(item));
    },
    limiteMaximoMargem() {
      const config = this.$store.state.configPadrao || {};
      return (config.limiteMaximoMargem * 1 || 0) + (config.limiteMaximoDivergenciaMargemOrc * 1 || 0);
    },
    recalculandoMargem() {
      return this.items.some((item) => this.recalculandoMargemItem(item));
    },
    podeSalvarItens() {
      return (
        (this.temAlteracaoQuantidade || this.temAlteracaoMargem) &&
        !this.temQuantidadeInvalida &&
        !this.temMargemInvalida &&
        !this.recalculandoMargem
      );
    },
    temAlteracoesPendentes() {
      return this.temAlteracaoQuantidade || this.temAlteracaoMargem;
    },
  },
  methods: {
    valorOrdenacao(item, key) {
      if (key === "valor_unit_total") {
        return this.valorVendaUnitario(item);
      }
      return item[key];
    },
    compareSortValues(a, b) {
      const aEmpty = a === null || a === undefined || a === "";
      const bEmpty = b === null || b === undefined || b === "";

      if (aEmpty && bEmpty) {
        return 0;
      }
      if (aEmpty) {
        return -1;
      }
      if (bEmpty) {
        return 1;
      }

      const aNum = Number(a);
      const bNum = Number(b);
      const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);

      if (bothNumeric) {
        if (aNum < bNum) {
          return -1;
        }
        if (aNum > bNum) {
          return 1;
        }
        return 0;
      }

      return String(a).localeCompare(String(b), "pt-BR", {
        numeric: true,
        sensitivity: "base",
      });
    },
    sortHeaderClass(key) {
      if (this.localSortBy !== key) {
        return null;
      }
      return this.localSortDesc
        ? "orcamento-itens__group--sorted-desc"
        : "orcamento-itens__group--sorted-asc";
    },
    applySort(sortBy, sortDesc) {
      this.localSortBy = sortBy;
      this.localSortDesc = sortDesc;
      this.$emit("update:sortBy", sortBy);
      this.$emit("update:sortDirection", sortDesc ? "desc" : "asc");
      this.$emit("sort-changed", {
        sortBy,
        sortDesc,
      });
    },
    toggleSort(key) {
      const sortDesc = this.localSortBy === key ? !this.localSortDesc : false;
      this.applySort(key, sortDesc);
    },
    onTableSortChanged(event) {
      this.applySort(event.sortBy, !!event.sortDesc);
    },
    getComissao(item) {
      if (this.getComissaoFn) {
        return this.getComissaoFn(item) * 1;
      }
      return item.valor_comissao * 1 || 0;
    },
    ipiUnit(item) {
      return (item.valor_ipi * 1 || 0) + (item.valor_margem_ipi * 1 || 0);
    },
    stUnit(item) {
      return (item.valor_st * 1 || 0) + (item.valor_margem_st * 1 || 0);
    },
    totalCusto(item) {
      return (item.quantidade * 1 || 0) * (item.valor_unitario * 1 || 0);
    },
    totalTrib(item) {
      return (item.quantidade * 1 || 0) * (this.ipiUnit(item) + this.stUnit(item));
    },
    margemTotal(item) {
      return (item.quantidade * 1 || 0) * (item.margem_valor * 1 || 0);
    },
    premioUnit(item) {
      const qtde = item.quantidade * 1;
      if (!qtde) {
        return 0;
      }
      return this.getComissao(item) / qtde;
    },
    premioTotal(item) {
      return this.getComissao(item);
    },
    quantidadeExibida(item) {
      const digitada = this.quantidadesDigitadas[item.id];
      return digitada !== undefined ? digitada : item.quantidade;
    },
    parseQuantidade(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const qtde = Number(String(value).replace(",", "."));
      if (!Number.isFinite(qtde) || qtde <= 0) {
        return null;
      }
      return qtde;
    },
    quantidadeInformada(item) {
      if (this.quantidadesDigitadas[item.id] !== undefined) {
        return this.parseQuantidade(this.quantidadesDigitadas[item.id]);
      }
      const qtde = item.quantidade * 1;
      return qtde > 0 ? qtde : null;
    },
    quantidadeMultiploInvalido(item) {
      const qtde = this.quantidadeInformada(item);
      if (qtde === null) {
        return false;
      }
      const multiplo = item.multiplo * 1;
      if (!multiplo) {
        return false;
      }
      return qtde % multiplo > 0;
    },
    quantidadeBloqueiaSalvar(item) {
      if (
        this.quantidadesDigitadas[item.id] !== undefined &&
        this.parseQuantidade(this.quantidadesDigitadas[item.id]) === null
      ) {
        return true;
      }
      return this.quantidadeMultiploInvalido(item);
    },
    quantidadeState(item) {
      if (this.quantidadeBloqueiaSalvar(item) && this.quantidadesDigitadas[item.id] !== undefined) {
        return false;
      }
      if (this.quantidadeMultiploInvalido(item)) {
        return false;
      }
      return null;
    },
    valorVendaUnitario(item) {
      const unitario = parseFloat(item.valor_unitario) || 0;
      const margem = parseFloat(item.margem_valor) || 0;
      const ipi = (parseFloat(item.valor_ipi) || 0) + (parseFloat(item.valor_margem_ipi) || 0);
      const st = (parseFloat(item.valor_st) || 0) + (parseFloat(item.valor_margem_st) || 0);
      return unitario + margem + ipi + st;
    },
    criarBaseQuantidade(item) {
      const qtde = item.quantidade * 1 || 0;
      const totalPremio = item.valor_comissao * 1 || 0;
      return {
        quantidade: qtde,
        valor_total: item.valor_total * 1,
        valor_comissao: totalPremio,
        comissaoUnitaria: qtde > 0 ? totalPremio / qtde : 0,
        margem_valor: item.margem_valor * 1 || 0,
        margem_percentual: item.margem_percentual * 1 || 0,
        valor_ipi: item.valor_ipi * 1 || 0,
        valor_st: item.valor_st * 1 || 0,
        valor_margem_ipi: item.valor_margem_ipi * 1 || 0,
        valor_margem_st: item.valor_margem_st * 1 || 0,
        bo_valida_minimo: item.bo_valida_minimo,
        vl_liquido_tab_coorp: item.vl_liquido_tab_coorp,
      };
    },
    baseDoItem(item) {
      if (!this.quantidadesBase[item.id]) {
        this.$set(this.quantidadesBase, item.id, this.criarBaseQuantidade(item));
      }
      return this.quantidadesBase[item.id];
    },
    onQuantidadeBlur(item) {
      const digitada = this.quantidadesDigitadas[item.id];
      if (digitada === undefined) {
        return;
      }
      if (this.parseQuantidade(digitada) === null) {
        this.$delete(this.quantidadesDigitadas, item.id);
      }
    },
    onQuantidadeInput(item, value) {
      this.$set(this.quantidadesDigitadas, item.id, value);
      const qtde = this.parseQuantidade(value);
      if (qtde === null) {
        return;
      }
      item.quantidade = qtde;
      this.aplicarValoresCalculados(item);
    },
    arredondar(value, casas) {
      const numero = typeof value === "number" ? value : this.parseNumero(value);
      if (numero === null || !Number.isFinite(numero)) {
        return 0;
      }
      const fator = Math.pow(10, casas);
      return Math.round(numero * fator) / fator;
    },
    parseNumero(value) {
      if (value === null || value === undefined || String(value).trim() === "") {
        return null;
      }
      let texto = String(value).trim();
      if (texto.includes(",") && texto.includes(".")) {
        texto = texto.replace(/\./g, "").replace(",", ".");
      } else if (texto.includes(",")) {
        texto = texto.replace(",", ".");
      }
      const numero = Number(texto);
      if (!Number.isFinite(numero) || numero < 0) {
        return null;
      }
      return numero;
    },
    formatPercent(value) {
      const numero = Number(value) || 0;
      return (
        numero.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + "%"
      );
    },
    margemValorExibida(item) {
      const digitada = this.margensValorDigitadas[item.id];
      if (digitada !== undefined) {
        return this.limitarDecimal102Texto(digitada);
      }
      return this.limitarDecimal102(item.margem_valor).toFixed(2);
    },
    limitarDecimal102(value) {
      const numero = this.arredondar(value, 2);
      const maximo = 99999999.99;
      if (numero > maximo) {
        return maximo;
      }
      return numero;
    },
    limitarDecimal102Texto(value) {
      let texto = this.limitarDecimaisVisuais(value, 2);
      if (!texto) {
        return texto;
      }
      const indice = texto.search(/[.,]/);
      if (indice === -1) {
        return texto.replace(/\D/g, "").slice(0, 8);
      }
      const inteiro = texto.slice(0, indice).replace(/\D/g, "").slice(0, 8);
      return inteiro + texto.slice(indice);
    },
    normalizarMargemValor(item) {
      item.margem_valor = this.limitarDecimal102(item.margem_valor);
    },
    limitarDecimaisVisuais(value, casas) {
      let texto = String(value == null ? "" : value).trim();
      if (texto.includes(",") && texto.includes(".")) {
        texto = texto.replace(/\./g, "");
      }
      const indice = texto.search(/[.,]/);
      if (indice === -1) {
        return texto;
      }
      return texto.slice(0, indice + 1 + casas);
    },
    formatarPercentualVisivel(value) {
      return this.arredondar(value, 2).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    percentualEfetivo(item) {
      const percentual = item.margem_percentual * 1 || 0;
      if (percentual > 0) {
        return percentual;
      }
      const unitario = item.valor_unitario * 1 || 0;
      const margem = item.margem_valor * 1 || 0;
      if (!unitario || !margem) {
        return 0;
      }
      return this.arredondar((margem / unitario) * 100, 2);
    },
    garantirPercentual(item) {
      const efetivo = this.percentualEfetivo(item);
      if ((item.margem_percentual * 1 || 0) !== efetivo) {
        item.margem_percentual = efetivo;
      }
    },
    margemPercentualExibida(item) {
      const digitada = this.margensPercentualDigitadas[item.id];
      if (digitada !== undefined) {
        return this.limitarDecimaisVisuais(digitada, 2);
      }
      return this.formatarPercentualVisivel(this.percentualEfetivo(item));
    },
    margemValorDigitadaInvalida(item) {
      return (
        this.margensValorDigitadas[item.id] !== undefined &&
        this.parseNumero(this.margensValorDigitadas[item.id]) === null
      );
    },
    margemPercentualDigitadaInvalida(item) {
      return (
        this.margensPercentualDigitadas[item.id] !== undefined &&
        this.parseNumero(this.margensPercentualDigitadas[item.id]) === null
      );
    },
    margemAlterada(item) {
      const base = this.quantidadesBase[item.id];
      if (!base) {
        return false;
      }
      return (
        this.arredondar(item.margem_valor, 2) !== this.arredondar(base.margem_valor, 2) ||
        this.arredondar(item.margem_percentual, 2) !== this.arredondar(base.margem_percentual, 2)
      );
    },
    margemAbaixoPiso(item) {
      if (item.bo_registro != "false") {
        return false;
      }
      const piso = parseFloat(item.vl_liquido_tab_coorp) || 0;
      if (!piso) {
        return false;
      }
      const unitario = parseFloat(item.valor_unitario) || 0;
      const margem = parseFloat(item.margem_valor) || 0;
      return piso > unitario + margem;
    },
    margemForaLimite(item) {
      if (this.margemAbaixoPiso(item)) {
        return true;
      }
      if (item.bo_valida_minimo == "S") {
        return false;
      }
      const limite = this.limiteMaximoMargem;
      const percentual = this.arredondar(item.margem_percentual, 2);
      if (percentual > limite) {
        return true;
      }
      const unitario = item.valor_unitario * 1 || 0;
      const limiteValor = this.arredondar(unitario * (limite / 100), 2);
      return (item.margem_valor * 1 || 0) > limiteValor;
    },
    margemBloqueiaSalvar(item) {
      if (this.margemValorDigitadaInvalida(item) || this.margemPercentualDigitadaInvalida(item)) {
        return true;
      }
      return this.margemAlterada(item) && this.margemForaLimite(item);
    },
    margemValorState(item) {
      if (this.margemValorDigitadaInvalida(item)) {
        return false;
      }
      if (this.margemAlterada(item) && this.margemForaLimite(item)) {
        return false;
      }
      return null;
    },
    margemPercentualState(item) {
      if (this.margemPercentualDigitadaInvalida(item)) {
        return false;
      }
      if (this.margemAlterada(item) && this.margemForaLimite(item)) {
        return false;
      }
      return null;
    },
    sincronizarMargemPeloValor(item, margem) {
      const unitario = item.valor_unitario * 1 || 0;
      item.margem_valor = margem;
      item.margem_percentual = unitario ? (margem / unitario) * 100 : 0;
    },
    sincronizarMargemPeloPercentual(item, percentual) {
      const unitario = item.valor_unitario * 1 || 0;
      item.margem_percentual = percentual;
      item.margem_valor = this.limitarDecimal102(unitario * (percentual / 100));
    },
    aplicarValoresCalculados(item) {
      const base = this.baseDoItem(item);
      const qtde = item.quantidade * 1;
      const qtdeIgual = qtde === base.quantidade;
      const margemIgual = !this.margemAlterada(item);
      if (qtdeIgual) {
        item.quantidade = base.quantidade;
      }
      if (qtdeIgual && margemIgual) {
        item.margem_valor = base.margem_valor;
        item.margem_percentual = base.margem_percentual;
        item.valor_total = base.valor_total;
        item.valor_comissao = base.valor_comissao;
        return;
      }
      item.valor_comissao = this.arredondar(base.comissaoUnitaria * qtde, 2);
      item.valor_total = this.valorVendaUnitario(item) * qtde;
    },
    restaurarImpostosBase(item) {
      const base = this.baseDoItem(item);
      item.valor_ipi = base.valor_ipi;
      item.valor_st = base.valor_st;
      item.valor_margem_ipi = base.valor_margem_ipi;
      item.valor_margem_st = base.valor_margem_st;
      item.bo_valida_minimo = base.bo_valida_minimo;
      item.vl_liquido_tab_coorp = base.vl_liquido_tab_coorp;
    },
    itemTemImposto(item) {
      const base = this.quantidadesBase[item.id];
      const ipi = this.ipiUnit(item) || (base ? (base.valor_ipi * 1 || 0) + (base.valor_margem_ipi * 1 || 0) : 0);
      const st = this.stUnit(item) || (base ? (base.valor_st * 1 || 0) + (base.valor_margem_st * 1 || 0) : 0);
      return ipi > 0 || st > 0;
    },
    chaveItem(item) {
      return item && item.id != null ? String(item.id) : "";
    },
    limparEstadoRecalculoMargem() {
      Object.keys(this.recalculoMargemTimers).forEach((id) => {
        clearTimeout(this.recalculoMargemTimers[id]);
      });
      this.recalculoMargemTimers = {};
      this.recalculoMargemToken = {};
      this.recalculandoMargemIds = {};
      (this.items || []).forEach((item) => {
        if (item && item._recalculandoMargem) {
          this.$delete(item, "_recalculandoMargem");
        }
      });
    },
    marcarRecalculoMargem(item, ativo) {
      const chave = this.chaveItem(item);
      if (ativo) {
        this.$set(item, "_recalculandoMargem", true);
        if (chave) {
          this.$set(this.recalculandoMargemIds, chave, true);
        }
        return;
      }
      this.$delete(item, "_recalculandoMargem");
      if (chave) {
        this.$delete(this.recalculandoMargemIds, chave);
      }
    },
    cancelarRecalculoMargemTimer(item) {
      const chave = this.chaveItem(item);
      const timer = this.recalculoMargemTimers[chave];
      if (timer) {
        clearTimeout(timer);
        this.$delete(this.recalculoMargemTimers, chave);
      }
    },
    invalidarRecalculoMargem(item) {
      const chave = this.chaveItem(item);
      this.cancelarRecalculoMargemTimer(item);
      if (chave) {
        this.$set(this.recalculoMargemToken, chave, (this.recalculoMargemToken[chave] || 0) + 1);
      }
      this.marcarRecalculoMargem(item, false);
    },
    recalculandoMargemItem(item) {
      return !!item._recalculandoMargem;
    },
    agendarRecalculoMargemImpostos(item) {
      if (!this.itemTemImposto(item)) {
        this.invalidarRecalculoMargem(item);
        return;
      }
      const chave = this.chaveItem(item);
      this.cancelarRecalculoMargemTimer(item);
      if (chave) {
        this.$set(this.recalculoMargemToken, chave, (this.recalculoMargemToken[chave] || 0) + 1);
      }
      this.marcarRecalculoMargem(item, true);
      const timer = setTimeout(() => {
        this.$delete(this.recalculoMargemTimers, chave);
        this.recalcularMargemImpostos(item);
      }, 1000);
      this.$set(this.recalculoMargemTimers, chave, timer);
    },
    recalcularMargemImpostos(item) {
      const orcamento = this.$store.state.orcamento.dados || {};
      const cliente = orcamento.cliente_codigo;
      const filial = item.cd_filial;
      const orcamentoId = item.orcamento_id || orcamento.id;
      const chave = this.chaveItem(item);
      if (!this.itemTemImposto(item) || !cliente || !item.produto_codigo || !filial) {
        this.marcarRecalculoMargem(item, false);
        return;
      }
      const token = (this.recalculoMargemToken[chave] || 0) + 1;
      this.$set(this.recalculoMargemToken, chave, token);
      this.marcarRecalculoMargem(item, true);
      Produto.getValoresMargem(
        item.produto_codigo,
        cliente,
        item.margem_percentual,
        item.margem_valor,
        item.quantidade,
        filial,
        orcamentoId
      )
        .then((response) => {
          if (this.recalculoMargemToken[chave] !== token) {
            return;
          }
          const data = response.data.data || {};
          item.valor_ipi = data.valor_ipi;
          item.valor_st = data.valor_st;
          item.valor_margem_ipi = data.valor_margem_ipi;
          item.valor_margem_st = data.valor_margem_st;
          item.bo_valida_minimo = data.bo_valida_vl_minimo_tab_coorp;
          item.vl_liquido_tab_coorp = data.vl_liquido_tab_coorp;
          this.aplicarValoresCalculados(item);
        })
        .catch((e) => {
          if (this.recalculoMargemToken[chave] !== token) {
            return;
          }
          this.$helper.showErrorResponse(e);
        })
        .finally(() => {
          if (this.recalculoMargemToken[chave] !== token) {
            return;
          }
          this.marcarRecalculoMargem(item, false);
        });
    },
    restaurarMargemBase(item) {
      const base = this.baseDoItem(item);
      this.invalidarRecalculoMargem(item);
      item.margem_valor = base.margem_valor;
      item.margem_percentual = base.margem_percentual;
      this.restaurarImpostosBase(item);
      this.$delete(this.margensValorDigitadas, item.id);
      this.$delete(this.margensPercentualDigitadas, item.id);
      this.aplicarValoresCalculados(item);
    },
    onMargemValorInput(item, value) {
      const limitado = this.limitarDecimal102Texto(value);
      this.$set(this.margensValorDigitadas, item.id, limitado);
      if (this.margensPercentualDigitadas[item.id] !== undefined) {
        this.$delete(this.margensPercentualDigitadas, item.id);
      }
      const margem = this.parseNumero(limitado);
      if (margem === null) {
        this.invalidarRecalculoMargem(item);
        return;
      }
      this.sincronizarMargemPeloValor(item, this.limitarDecimal102(margem));
      this.aplicarValoresCalculados(item);
      this.tratarRecalculoAposMargem(item);
    },
    onMargemPercentualInput(item, value) {
      const limitado = this.limitarDecimaisVisuais(value, 2);
      this.$set(this.margensPercentualDigitadas, item.id, limitado);
      value = limitado;
      if (this.margensValorDigitadas[item.id] !== undefined) {
        this.$delete(this.margensValorDigitadas, item.id);
      }
      const percentual = this.parseNumero(value);
      if (percentual === null) {
        this.invalidarRecalculoMargem(item);
        return;
      }
      this.sincronizarMargemPeloPercentual(item, percentual);
      this.aplicarValoresCalculados(item);
      this.tratarRecalculoAposMargem(item);
    },
    tratarRecalculoAposMargem(item, reagendar = true) {
      if (!this.margemAlterada(item)) {
        this.invalidarRecalculoMargem(item);
        this.restaurarImpostosBase(item);
        this.aplicarValoresCalculados(item);
        return;
      }
      const chave = this.chaveItem(item);
      if (!reagendar && (this.recalculoMargemTimers[chave] || item._recalculandoMargem)) {
        return;
      }
      this.agendarRecalculoMargemImpostos(item);
    },
    onMargemValorBlur(item) {
      const digitada = this.margensValorDigitadas[item.id];
      if (digitada === undefined) {
        return;
      }
      if (this.parseNumero(digitada) === null) {
        this.restaurarMargemBase(item);
        return;
      }
      const margem = this.limitarDecimal102(this.parseNumero(digitada));
      const base = this.baseDoItem(item);
      if (margem === this.arredondar(base.margem_valor, 2)) {
        this.restaurarMargemBase(item);
        return;
      }
      this.sincronizarMargemPeloValor(item, margem);
      this.$delete(this.margensValorDigitadas, item.id);
      this.aplicarValoresCalculados(item);
      this.tratarRecalculoAposMargem(item, false);
    },
    onMargemPercentualBlur(item) {
      const digitada = this.margensPercentualDigitadas[item.id];
      if (digitada === undefined) {
        return;
      }
      if (this.parseNumero(digitada) === null) {
        this.restaurarMargemBase(item);
        return;
      }
      const percentual = this.arredondar(this.parseNumero(digitada), 2);
      const base = this.baseDoItem(item);
      if (percentual === this.arredondar(base.margem_percentual, 2)) {
        this.restaurarMargemBase(item);
        return;
      }
      this.sincronizarMargemPeloPercentual(item, percentual);
      this.$delete(this.margensPercentualDigitadas, item.id);
      this.aplicarValoresCalculados(item);
      this.tratarRecalculoAposMargem(item, false);
    },
    salvarQuantidades() {
      if (!this.podeSalvarItens || this.itensAlterados) {
        return;
      }
      const alterados = this.items.filter((item) => {
        const base = this.quantidadesBase[item.id];
        if (!base) {
          return false;
        }
        return item.quantidade * 1 !== base.quantidade * 1 || this.margemAlterada(item);
      });
      this.$emit("salvar-quantidades", alterados);
    },
    async confirmarCancelarAlteracoes() {
      if (this.itensAlterados) {
        return;
      }
      const confirmar = await this.$bvModal.msgBoxConfirm(
        "Deseja desfazer as alterações não salvas?",
        {
          title: "Atenção",
          size: "sm",
          centered: true,
          hideHeaderClose: false,
          contentClass: "orcamento-confirm-modal",
          headerClass: "orcamento-confirm-modal__header",
          bodyClass: "orcamento-confirm-modal__body",
          footerClass: "orcamento-confirm-modal__footer",
          okVariant: "orcamento-confirm-ok",
          cancelVariant: "orcamento-confirm-cancel",
          okTitle: "Sim",
          cancelTitle: "Não",
        }
      );
      if (!confirmar) {
        return;
      }
      this.cancelarAlteracoes();
    },
    cancelarAlteracoes() {
      if (this.itensAlterados) {
        return;
      }
      this.limparEstadoRecalculoMargem();
      this.items.forEach((item) => {
        const base = this.quantidadesBase[item.id];
        if (!base) {
          return;
        }
        item.quantidade = base.quantidade;
        item.valor_total = base.valor_total;
        item.valor_comissao = base.valor_comissao;
        item.margem_valor = base.margem_valor;
        item.margem_percentual = base.margem_percentual;
        item.valor_ipi = base.valor_ipi;
        item.valor_st = base.valor_st;
        item.valor_margem_ipi = base.valor_margem_ipi;
        item.valor_margem_st = base.valor_margem_st;
        item.bo_valida_minimo = base.bo_valida_minimo;
        item.vl_liquido_tab_coorp = base.vl_liquido_tab_coorp;
      });
      this.quantidadesDigitadas = {};
      this.margensValorDigitadas = {};
      this.margensPercentualDigitadas = {};
    },
  },
};
</script>

<style scoped>
.orcamento-itens {
  background: #fff;
  border: 1px solid #e4e7ea;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.orcamento-itens__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #edf0f2;
}

.orcamento-itens__title-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-left: 0.65rem;
  border-left: 3px solid #0075eb;
}

.orcamento-itens__title {
  font-size: 1rem;
  font-weight: 700;
  color: #23282c;
  line-height: 1.2;
}

.orcamento-itens__subtitle {
  font-size: 0.78rem;
  color: #8a94a6;
}

.orcamento-itens__header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.orcamento-itens__filial {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid #cfe2ff;
  background: #f3f8ff;
  color: #0075eb;
  font-size: 0.8rem;
  font-weight: 600;
}

.orcamento-itens__add-btn {
  font-weight: 600;
  padding: 0.45rem 0.95rem !important;
  border-radius: 8px !important;
  background-color: #7C3AED !important;
  border-color: #7C3AED !important;
}

.orcamento-itens__add-btn:hover:not(:disabled),
.orcamento-itens__add-btn:focus:not(:disabled) {
  background-color: #5804CA !important;
  border-color: #5804CA !important;
}

.orcamento-itens__body {
  overflow-x: auto;
}

.orcamento-itens__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 180px;
  color: #8a94a6;
  font-size: 1.1rem;
  font-weight: 600;
}

.orcamento-itens__table >>> table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.72rem;
}

.orcamento-itens__table >>> thead th {
  border-top: none;
  border-bottom: 1px solid #edf0f2;
  vertical-align: middle;
  white-space: nowrap;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.orcamento-itens__group {
  text-align: center !important;
  font-size: 0.68rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: none !important;
  padding: 0.45rem 0.5rem !important;
}

.orcamento-itens__group--solo {
  border-bottom: 1px solid #edf0f2 !important;
  vertical-align: middle !important;
}

.orcamento-itens__group--acoes {
  background: #fff !important;
}

.orcamento-itens__group--sortable {
  cursor: pointer;
  user-select: none;
}

.orcamento-itens__group--sortable:hover {
  filter: brightness(0.97);
}

.orcamento-itens__sort-icon {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 0.35rem;
  vertical-align: middle;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #adb5bd;
  opacity: 0.55;
}

.orcamento-itens__group--sorted-asc .orcamento-itens__sort-icon {
  border-top: 0;
  border-bottom: 5px solid currentColor;
  opacity: 1;
}

.orcamento-itens__group--sorted-desc .orcamento-itens__sort-icon {
  border-top-color: currentColor;
  opacity: 1;
}

.orcamento-itens__table >>> thead tr:nth-child(2) .orcamento-itens__th--spanned {
  display: none;
}

.orcamento-itens__group--custo {
  background-color: #EEF2F6 !important;
  color: #475569;
}

.orcamento-itens__group--custo > span {
  color: #191e25;
}

.orcamento-itens__table >>> .orcamento-itens__th--custo {
  background: #F8FAFC !important;
  color: #191e25;
  font-weight: 500;
}

.orcamento-itens__group--tributos {
  background-color: #FEF3C7 !important;
  color: #B85C14;
}

.orcamento-itens__table >>> .orcamento-itens__th--tributos {
  background: #FFF8E8 !important;
  color: rgb(180 83 9 / var(--tw-text-opacity, 1));
  font-weight: 500;
}

.orcamento-itens__group--margem {
  background-color: #D1FAE5 !important;
  color: #349778;
  font-weight: 500;
}

.orcamento-itens__table >>> .orcamento-itens__th--margem {
  background: #EAF8EF !important;
  color: rgb(4 120 87 / var(--tw-text-opacity, 1));
  font-weight: 500;
}

.orcamento-itens__group--premio {
  background-color: #E0F2FE !important;
  color: #1071A6;
  font-weight: 500;
}

.orcamento-itens__table >>> .orcamento-itens__th--premio {
  background: #EEF4FF !important;
  color: rgb(3 105 161 / var(--tw-text-opacity, 1));
  font-weight: 500;
}

.orcamento-itens__group--unitario,
.orcamento-itens__table >>> .orcamento-itens__th--unitario {
  background: #c7d2fe !important;
  color: #312e81 !important;
}

.orcamento-itens__group--venda,
.orcamento-itens__table >>> .orcamento-itens__th--venda {
  background: rgb(186 230 253 / var(--tw-bg-opacity, 1)) !important;
  color: rgb(7 89 133 / var(--tw-text-opacity, 1)) !important;
}

.orcamento-itens__table >>> tbody {
  font-size: 0.72rem;
}

.orcamento-itens__table >>> tbody td {
  border-top: 1px solid #edf0f2;
  vertical-align: middle;
  padding: 0.55rem 0.5rem;
}

.orcamento-itens__table >>> tfoot th {
  /* background: #f8f9fa; */
  border-top: 1px solid #dee2e6;
  vertical-align: middle;
  padding: 0.65rem 0.5rem;
  font-weight: 700;
}

.orcamento-itens__table >>> tfoot .orcamento-itens__th--custo,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--tributos,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--margem,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--premio,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--unitario,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--venda {
  text-align: right !important;
}

.orcamento-itens__table >>> tfoot .orcamento-itens__th--spanned.text-center {
  text-align: center !important;
}

.orcamento-itens__table >>> tfoot .orcamento-itens__th--unitario.orcamento-itens__th--spanned,
.orcamento-itens__table >>> tfoot .orcamento-itens__th--venda.orcamento-itens__th--spanned {
  text-align: right !important;
}

.orcamento-itens__produto {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.15rem;
  width: 100%;
  min-width: 220px;
}

.orcamento-itens__produto-linha {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.orcamento-itens__estoque {
  align-self: flex-end;
  white-space: nowrap;
  text-align: right;
  color: #1b7a45;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
}

.orcamento-itens__codigo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: #e8eef5;
  color: rgb(38 56 213 / var(--tw-text-opacity, 1));
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

.orcamento-itens__nome {
  color: #23282c;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 1.25;
  word-break: break-word;
}

.orcamento-itens__num {
  font-weight: 700;
  color: #23282c;
}

.orcamento-itens__money {
  font-weight: 600;
  /* color: #23282c; */
  color: rgb(95 103 114);
  white-space: nowrap;
}

.orcamento-itens__money--custo-total {
  color: #191e25;
}

.orcamento-itens__money--trib {
  color: #e67e22;
}

.orcamento-itens__money--margem {
  color: #1b7a45;
}

.orcamento-itens__money--premio {
  color: #1071a6;
}

.orcamento-itens__money--venda {
  color: rgb(3 105 161 / var(--tw-text-opacity, 1));
  font-weight: 700;
}

.orcamento-itens__money--unitario {
  color: #55559c;
  font-weight: 700;
}

.orcamento-itens__money--venda:before,
.orcamento-itens__table >>> tfoot .orcamento-itens__money:before {
  content: "R$ ";
}

.orcamento-itens__totais-label {
  font-weight: 700;
  text-transform: uppercase;
  color: #23282c;
}

.orcamento-itens__acoes {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.orcamento-itens__acao {
  width: 28px;
  height: 28px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px !important;
  background: #fff !important;
}

.orcamento-itens__acao--delete {
  border: 1px solid #f5c2c7 !important;
  color: #dc3545 !important;
}

.orcamento-itens__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.55rem 1rem 0.75rem;
  border-top: 1px solid #edf0f2;
}

.orcamento-itens__note {
  color: #8a94a6;
  font-size: 0.72rem;
}

.orcamento-itens__save-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.75rem;
  flex-shrink: 0;
}

.orcamento-itens__pendente {
  color: #b45309;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  animation: orcamento-itens-pendente-pulse 1.4s ease-in-out infinite;
}

.orcamento-itens__pendente-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: #dc3545;
  box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  animation: orcamento-itens-pendente-dot 1.4s ease-out infinite;
}

@keyframes orcamento-itens-pendente-dot {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(220, 53, 69, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

@keyframes orcamento-itens-pendente-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

.orcamento-itens__qtde {
  width: 4.5rem;
  margin: 0 auto;
  height: auto;
  padding: 0.15rem 0.35rem;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 700;
  border-color: #c5c5c5;
}

.orcamento-itens__qtde:focus {
  border-color: #c5c5c5;
}

.orcamento-itens__margem {
  width: 5.5rem;
  margin-left: auto;
  height: auto;
  padding: 0.15rem 0.35rem;
  text-align: right;
  font-size: 0.72rem;
  font-weight: 700;
  border-color: #c5c5c5;
}

.orcamento-itens__margem:focus {
  border-color: #c5c5c5;
}

.orcamento-itens__percent {
  position: relative;
  width: 5.5rem;
  margin-left: auto;
}

.orcamento-itens__margem--percent {
  width: 100%;
  margin-left: 0;
  padding-right: 0.95rem;
}

.orcamento-itens__percent-sign {
  position: absolute;
  top: 50%;
  right: 0.3rem;
  transform: translateY(-50%);
  color: #1b7a45 !important;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
}

.orcamento-itens__qtde-erro {
  color: #dc3545;
  font-size: 0.72rem;
}

.orcamento-itens__save-btn {
  font-weight: 600;
  border-radius: 8px !important;
  background: #e8f7ee !important;
  border: 1px solid #b7e4c7 !important;
  color: #1b7a45 !important;
}

.orcamento-itens__save-btn:hover:not(:disabled),
.orcamento-itens__save-btn:focus:not(:disabled) {
  background: #d8f0e1 !important;
  color: #146338 !important;
}

.orcamento-itens__cancel-btn {
  font-weight: 600;
  border-radius: 8px !important;
  background: #fff !important;
  border: 1px solid #d0d5dd !important;
  color: #5f6772 !important;
}

.orcamento-itens__cancel-btn:hover:not(:disabled),
.orcamento-itens__cancel-btn:focus:not(:disabled) {
  background: #f8f9fa !important;
  color: #23282c !important;
}

.orcamento-itens__spinner {
  display: inline-flex;
  color: #8a94a6;
}
</style>
